"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "@/lib/auth/session";
import { findComingSoonTopic, type ComingSoonTopic } from "@/lib/coming-soon";
import { ComingSoonDialog } from "./coming-soon-dialog";

/**
 * Shows the "coming soon" popup for unreleased practice tracks, to signed-in users only.
 * Two entry points, both handled here so no link in the app needs to change:
 *  1. Clicking any link to a coming-soon route: navigation is cancelled and the popup opens.
 *  2. Landing on the route directly (typed URL, bookmark, back button): popup opens over the page.
 * Signed-out visitors are untouched and still see the normal public page.
 */
export function ComingSoonProvider({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const pathname = usePathname();
  const signedIn = status === "authenticated";

  const [clickedTopic, setClickedTopic] = useState<ComingSoonTopic | null>(null);
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);

  // Forget a dismissal once the visitor moves to another route, so returning shows it again.
  if (dismissedPath !== null && dismissedPath !== pathname) setDismissedPath(null);

  useEffect(() => {
    if (!signedIn) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target !== "_self") return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const topic = findComingSoonTopic(url.pathname);
      if (!topic) return;
      // Capture phase: runs before Next.js <Link> handles the click.
      event.preventDefault();
      event.stopPropagation();
      setClickedTopic(topic);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [signedIn]);

  const close = useCallback(() => {
    setClickedTopic(null);
    setDismissedPath(pathname);
  }, [pathname]);

  const arrivalTopic = signedIn && dismissedPath !== pathname ? findComingSoonTopic(pathname) : null;
  const visibleTopic = signedIn ? (clickedTopic ?? arrivalTopic) : null;

  return (
    <>
      {children}
      {visibleTopic && <ComingSoonDialog key={visibleTopic.slug} topic={visibleTopic} onClose={close} />}
    </>
  );
}
